import Header from "@/components/Header";
import { Link } from "react-router-dom";
import { ShieldCheck, TrendingUp, Trophy, GraduationCap, Users, Award, Target, MapPin, Star } from "lucide-react";

const COMPONENTS = [
  {
    icon: <MapPin size={22} />,
    label: "Playing Pedigree",
    cap: 18,
    detail:
      "Your playing résumé from HS through pro. Heavily boosted if you developed in a recognized lacrosse hotbed, played for an elite HS club program, or learned the game under a HOF HS or club coach. College All-American, college HOF, top-25 national position rank and pro experience also count.",
    boost:
      "Add your HS team, HS city/state, HS coach + whether they were HOF, and the Club program you played for in HS plus that club coach.",
  },
  {
    icon: <TrendingUp size={22} />,
    label: "Coaching Tenure × Competition",
    cap: 20,
    detail:
      "Years coached, weighted by the division you coached at (D1 = 1.0, NAIA = 0.80, D2 = 0.75, D3 = 0.65 ...) and your role (Head Coach > Coordinator > Assistant > GA / Volunteer).",
    boost: "Add every coaching stint with accurate role, division, start/end year.",
  },
  {
    icon: <Trophy size={22} />,
    label: "Career Record vs Competition",
    cap: 15,
    detail:
      "Your career W-L record, but division-weighted. A 12-2 D1 season counts much more than a 14-0 club season.",
    boost: "Add wins and losses per stint — the engine division-weights automatically.",
  },
  {
    icon: <Award size={22} />,
    label: "Championship Tier",
    cap: 14,
    detail:
      "National championships > Final / Division finals > Conference titles > State / tournament titles. The tier you actually won matters.",
    boost:
      "List each championship per stint. If the championship was won while you were an assistant, mark it as such (it still earns CIR points in the Assistant section).",
  },
  {
    icon: <ShieldCheck size={22} />,
    label: "Recognition & National Ranking",
    cap: 7,
    detail:
      "Coach of the Year awards, accolades, national-ranking of your teams (Top-5 / Top-25 finishes), HOF memberships (USILA, NILCA, IMLCA, IWLCA, etc.).",
    boost: "Add CoY awards as accolades and list any coaching HOF memberships.",
  },
  {
    icon: <Users size={22} />,
    label: "Mentor Tree (Coaching Pedigree)",
    cap: 10,
    detail:
      "Who you learned the game from. HOF mentors carry 3× weight; national-champion mentors stack another 2× on top. Direct assistant-under-the-HC stints get an additional 1.5× multiplier. Coaching for an 'Elite' program adds further. Up to 10 mentors counted.",
    boost:
      "Add every HC you served under, mark HOF / National Champion / # of championships / program tier (Elite, National, Regional) / whether you were their direct assistant and how many years.",
  },
  {
    icon: <Target size={22} />,
    label: "Assistant-Coach Wins",
    cap: 5,
    detail:
      "Championships and wins you earned while serving as an assistant or coordinator. Tier-weighted (national > division > conference).",
    boost:
      "On each assistant stint, list wins/losses and mark championships as 'as_assistant'.",
  },
  {
    icon: <GraduationCap size={22} />,
    label: "Education",
    cap: 5,
    detail: "Bachelor's degree + GPA, and Master's degree. Caps at 5.",
    boost: "Add your degree, school, GPA, and Master's if applicable.",
  },
  {
    icon: <Star size={22} />,
    label: "Peer Endorsements (HOF-weighted)",
    cap: 6,
    detail:
      "Coaches you've played for or worked with vouch for you. HOF endorsers carry 4 pts each; national-champion endorsers 2 pts; standard peers 0.5 pts. Capped at 6 to keep the score grounded in evidence, not popularity.",
    boost:
      "From your dashboard, tap 'Request an Endorsement' and share the link with the most credible coaches you've worked with. One HOF endorsement = +4 CIR.",
  },
];

const TIERS = [
  { label: "Elite (Top 1%)", from: 88, color: "text-blaze", glow: "shadow-[0_0_30px_rgba(255,59,48,0.4)]" },
  { label: "Tier 1", from: 75, color: "text-volt" },
  { label: "Tier 2", from: 60, color: "text-yellow-400" },
  { label: "Tier 3", from: 45, color: "text-blue-400" },
  { label: "Emerging", from: 0, color: "text-neutral-400" },
];

export default function CIRExplainer() {
  return (
    <div className="min-h-screen bg-appbg text-white">
      <Header />
      <main className="px-4 md:px-8 lg:px-12 py-10 max-w-5xl mx-auto" data-testid="cir-explainer-page">
        <div className="text-xs tracking-widest text-blaze">PATENT PENDING</div>
        <h1 className="heading text-4xl sm:text-5xl lg:text-6xl mt-2 leading-none">
          THE COACH IMPACT <span className="text-blaze">RATING™</span>
        </h1>
        <p className="mt-4 text-sm md:text-base text-neutral-300 max-w-3xl">
          CIR is CoachConnect AI&apos;s transparent, deterministic 0-100 score for every lacrosse coach
          on the platform. No black-box magic — eight components, fixed weights, fully explainable.
          Programs use CIR to filter candidates instantly; coaches use it to see exactly what&apos;s
          missing from their pedigree.
        </p>

        <div className="mt-8 grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="cir-tier-bar">
          {TIERS.map((t) => (
            <div
              key={t.label}
              className={`border border-neutral-800 p-4 bg-appcard ${t.glow || ""}`}
              data-testid={`tier-${t.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            >
              <div className={`heading text-2xl ${t.color}`}>{t.from}+</div>
              <div className="text-[10px] tracking-widest text-neutral-400 mt-1">{t.label.toUpperCase()}</div>
            </div>
          ))}
        </div>

        <h2 className="heading text-2xl md:text-3xl mt-12">NINE COMPONENTS · 100 POINTS TOTAL</h2>
        <div className="mt-6 grid gap-4 md:grid-cols-2" data-testid="cir-components-grid">
          {COMPONENTS.map((c, i) => (
            <div
              key={c.label}
              data-testid={`cir-component-${i}`}
              className="border border-neutral-800 p-5 bg-appcard hover:border-volt transition-colors"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-volt">{c.icon}<span className="heading text-base">{c.label.toUpperCase()}</span></div>
                <div className="heading text-2xl text-blaze">{c.cap}<span className="text-xs text-neutral-500"> max</span></div>
              </div>
              <p className="mt-3 text-sm text-neutral-300 leading-relaxed">{c.detail}</p>
              <div className="mt-3 text-xs text-neutral-400 border-l-2 border-volt pl-3">
                <span className="text-volt heading text-[11px] tracking-widest">BOOST IT —</span> {c.boost}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 border border-blaze/40 p-6 bg-blaze/5">
          <h3 className="heading text-xl text-blaze">HOW TO RAISE YOUR CIR TODAY</h3>
          <ol className="mt-3 space-y-2 text-sm text-neutral-200 list-decimal list-inside">
            <li>Open your Coach Profile and fill the <b>HS Playing Pedigree</b> section — your HS team, city/state, HS coach, the Club program you played for in HS, and that club coach. Mark HOF status where applicable.</li>
            <li>Add every coaching mentor you served under in the <b>Mentor Tree</b> (up to 10). Flag HOF, national championships, # of championships, program tier (Elite / National / Regional), whether you were the direct assistant, and years under each mentor.</li>
            <li>Complete each coaching stint with division, wins/losses, championships, and accolades.</li>
            <li>Add Coach-of-the-Year honors as accolades, and any HOF memberships you carry.</li>
            <li>Click <b>SAVE & MATCH ME</b>. CIR recalculates instantly.</li>
          </ol>
          <Link
            to="/coach/profile"
            data-testid="cir-go-to-profile-btn"
            className="mt-5 inline-block bg-blaze text-white heading text-base px-6 py-3 min-h-[48px] hover:glow-blaze transition-all"
          >
            EDIT MY PROFILE →
          </Link>
        </div>

        <p className="mt-10 text-xs text-neutral-500">
          CIR™ (Coach Impact Rating) is a patent-pending composite developed by CoachConnect AI.
          Weights and components above are tuned for Lacrosse and will be re-tuned per sport as the
          platform expands. All inputs are coach-asserted; verification is layered in via the
          EntityResolutionAgent and public records.
        </p>
      </main>
    </div>
  );
}
